// AES-1 — Authorship Establishment (cold start). FIRST-ENCOUNTER UX: orientation BEFORE any verification
// task; simplified task screen; short recovery flow on later attempts; persistent "About Authorship".
// Event-driven global overlay (renders nothing unless the backend reports authorship activity). The Writing Studio
// LEADS. Restrained styling — no cheating/AI/suspicion language on the first screen. This file is
// UX/copy/sequence only; AES/WPI backend logic is untouched.
import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, CheckCircle2, CircleSlash, Info, Compass as CompassIcon } from "lucide-react";
import { API, AUTH_TOKEN_KEY } from "@/lib/api";
import { getTestMode, TEST_MODE_EVENT } from "@/lib/testMode";

const BRAND = "#8C3A2A";
const auth = () => { const t = localStorage.getItem(AUTH_TOKEN_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };
const jpost = (u, b) => fetch(u, { method: "POST", headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify(b || {}) }).then((r) => r.json()).catch(() => null);
const jget = (u) => fetch(u, { headers: auth() }).then((r) => r.json()).catch(() => null);
const sessionId = () => { try { return (JSON.parse(localStorage.getItem("compass_student_session") || "{}") || {}).id || null; } catch (e) { return null; } };

// ── First-encounter orientation copy (positive purpose + process; NO AI mention) ──
const ORIENT = ("The Writing Studio is designed to help you develop your own writing. As you work, The Writing Studio also builds "
  + "a record connecting your writing to the thinking and writing you do along the way.\n\n"
  + "When The Writing Studio has been able to observe that process, authorship can usually be established from the "
  + "work you have already done.\n\n"
  + "Sometimes, however, The Writing Studio receives developed writing before it has seen how that writing developed. "
  + "In that situation, The Writing Studio asks you to work with some of the thinking in your writing so that it can "
  + "establish the connection between the writing and your own work.\n\nThat's what is happening here.");
const WHAT = ("The Writing Studio will ask you 1–3 brief questions about your paragraph. The questions will ask you to "
  + "explain or work with important relationships among your ideas.\n\nThere aren't particular words "
  + "The Writing Studio is looking for. Show, in your own words, that you understand the thinking represented in your "
  + "writing and can work with it.");

// ── About Authorship (fuller; includes what The Writing Studio does/does not conclude) ──
const ABOUT = ("The Writing Studio is designed to help you develop as a writer—not to do your thinking or writing for "
  + "you. For that reason, The Writing Studio keeps track of how your writing develops as you work.\n\n"
  + "When The Writing Studio has observed you developing your ideas and writing, it can usually connect your current "
  + "work to that process. But sometimes developed writing appears without enough earlier work for The Writing Studio "
  + "to make that connection. When this happens, The Writing Studio may ask you a few questions about the thinking in "
  + "your writing.\n\n"
  + "To establish authorship, The Writing Studio may ask you to explain a relationship between ideas, think through "
  + "what would happen if part of your reasoning changed, or make a small revision that reflects your "
  + "thinking. These questions are designed to see whether you can work with the ideas and relationships "
  + "represented in the writing.\n\n"
  + "If you demonstrate sufficient control of the work, The Writing Studio records the authorship of the writing as "
  + "established. If The Writing Studio cannot establish authorship, it records the authorship as unresolved. Your "
  + "teacher can see this status.\n\n"
  + "The Writing Studio does not conclude from this process that you used AI in your writing. It only determines "
  + "whether it has sufficient evidence to connect your revised writing to the work you had produced up "
  + "to that point.");

const ORIENT_KEY = "compass_aes_oriented";

const Shell = ({ children }) => (
  <div className="fixed inset-0 z-[66] flex items-center justify-center bg-stone-900/30 p-4">
    <div className="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

export default function AuthorshipEstablishment() {
  const [status, setStatus] = useState(null);
  const [screen, setScreen] = useState(null);   // orient | challenge | established | unresolved
  const [recovery, setRecovery] = useState(false);
  const [field, setField] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [testMode, setTM] = useState(getTestMode());

  useEffect(() => {
    const sync = () => setTM(getTestMode());
    window.addEventListener(TEST_MODE_EVENT, sync);
    return () => window.removeEventListener(TEST_MODE_EVENT, sync);
  }, []);

  const poll = useCallback(async () => {
    const sid = sessionId(); if (!sid) { setStatus(null); return null; }
    const s = await jget(`${API}/sessions/${sid}/aes-status`); setStatus(s); return s;
  }, []);

  useEffect(() => { let a = true; const t = async () => { if (a) await poll(); }; t(); const iv = setInterval(t, 3000); return () => { a = false; clearInterval(iv); }; }, [poll]);

  const sid = sessionId();

  useEffect(() => {
    if (!status) return;
    const active = status.active && status.state === "UNESTABLISHED";
    if (active && screen === null) {
      const oriented = localStorage.getItem(ORIENT_KEY) === sid;
      if (oriented) { setRecovery(true); setScreen("challenge"); }   // later encounter -> short flow
      else { setRecovery(false); setScreen("orient"); }              // first encounter -> orientation first
    }
    if (!active && (screen === "orient" || screen === "challenge")) setScreen(null);
  }, [status, screen, sid]);

  useEffect(() => { const h = () => { poll(); }; window.addEventListener("aes-required", h); return () => window.removeEventListener("aes-required", h); }, [poll]);

  const q = status?.current_question;
  const eventId = status?.event_id;
  const noPaste = (e) => { if (getTestMode()) return; e.preventDefault(); setNote("Please answer in your own words — pasting is off here."); };

  const begin = () => { try { localStorage.setItem(ORIENT_KEY, sid); } catch (e) {} setScreen("challenge"); };

  const submit = async () => {
    if (!field.trim()) { setNote("Please write a sentence or two."); return; }
    setBusy(true);
    const r = await jpost(`${API}/sessions/${sid}/aes/respond`, { event_id: eventId, answer: field });
    setBusy(false); setField(""); setNote("");
    if (r?.state === "UNESTABLISHED") { await poll(); return; }
    if (r?.state === "ESTABLISHED") setScreen("established");
    else setScreen("unresolved");
  };

  const continueAnyway = async () => { setBusy(true); await jpost(`${API}/sessions/${sid}/aes/continue-anyway`, {}); setBusy(false); setScreen("unresolved"); };

  const reestablish = async () => {
    setBusy(true);
    const r = await jpost(`${API}/sessions/${sid}/aes/reestablish`, {});
    setBusy(false);
    if (r?.event_id) { await poll(); setRecovery(true); setScreen("challenge"); }
    else setNote(r?.detail || "Couldn't start an authorship check right now.");
  };

  const finishAndResume = () => { setScreen(null); window.location.reload(); };
  const AboutLink = ({ cls }) => (
    <button data-testid="aes-about-link" onClick={() => setShowAbout(true)} className={cls || "inline-flex items-center gap-1 text-[12px] text-stone-500 underline decoration-dotted hover:text-[#8C3A2A]"}>
      <Info className="h-3 w-3" /> About Authorship
    </button>
  );

  if (!sid || !status || testMode) return null;   // TEST MODE: suppress the authorship/AI check overlay

  return (
    <>
      {note && <div data-testid="aes-note" className="fixed bottom-16 left-1/2 z-[70] -translate-x-1/2 max-w-md rounded-md bg-stone-800 px-3 py-1.5 text-center text-[12px] text-white">{note}</div>}

      {/* Persistent, unobtrusive status chips when no card is open. */}
      {screen === null && status.state === "UNRESOLVED" && (
        <div data-testid="aes-status-chip" className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 flex items-center gap-3 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-[12px] text-stone-600 shadow">
          <span>Authorship unresolved for this writing.</span>
          <button data-testid="aes-reestablish-btn" disabled={busy} onClick={reestablish} className="rounded-full px-2.5 py-0.5 text-white" style={{ background: BRAND }}>{busy ? "…" : "Establish authorship"}</button>
          <AboutLink />
        </div>
      )}
      {screen === null && status.state === "ESTABLISHED" && (
        <div data-testid="aes-status-chip" className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 flex items-center gap-3 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[12px] text-emerald-800 shadow">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Authorship established.</span>
          <AboutLink cls="inline-flex items-center gap-1 text-[12px] text-emerald-800/80 underline decoration-dotted" />
        </div>
      )}

      {/* STEP 1 — first-encounter ORIENTATION (positive purpose + process; no AI mention, no task yet). */}
      {screen === "orient" && (
        <Shell><div data-testid="aes-orient">
          <div className="flex items-center gap-2 text-[#8C3A2A]"><CompassIcon className="h-4 w-4" /><p className="font-mono text-[11px] uppercase tracking-[0.18em]">How The Writing Studio establishes authorship</p></div>
          {status.continuity_message
            ? <><p data-testid="aes-continuity-heading" className="mt-3 font-serif text-2xl text-stone-900">We need to verify authorship</p><p data-testid="aes-continuity-message" className="mt-2 text-[14px] leading-relaxed text-stone-700">{status.continuity_message}</p></>
            : <p className="mt-3 whitespace-pre-line text-[14px] leading-relaxed text-stone-700">{ORIENT}</p>}
          <div className="mt-5 rounded-lg border border-stone-200 bg-stone-50 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500">What will happen?</p>
            <p className="mt-2 whitespace-pre-line text-[13.5px] leading-relaxed text-stone-700">{WHAT}</p>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <AboutLink />
            <button data-testid="aes-orient-begin" onClick={begin} className="rounded-md px-5 py-2.5 text-[14px] text-white" style={{ background: BRAND }}>Begin authorship check</button>
          </div>
        </div></Shell>
      )}

      {/* STEP 2 — verification task (simplified; student already oriented). */}
      {screen === "challenge" && q && (
        <Shell><div data-testid="aes-card">
          <div className="flex items-center gap-2 text-[#8C3A2A]"><ShieldCheck className="h-4 w-4" /><p data-testid="aes-heading" className="font-mono text-[11px] uppercase tracking-[0.18em]">Establishing your authorship</p></div>
          {status.continuity_message && <p data-testid="aes-continuity-line" className="mt-3 text-[13.5px] leading-relaxed text-stone-600">{status.continuity_message}</p>}
          {!status.continuity_message && (recovery
            ? <p data-testid="aes-recovery-line" className="mt-3 text-[13.5px] leading-relaxed text-stone-600">The Writing Studio still needs enough evidence to connect this writing to your demonstrated work. Let's try again using your current paragraph.</p>
            : <p data-testid="aes-relationship-line" className="mt-3 text-[13.5px] leading-relaxed text-stone-600">Let's start with one relationship in your paragraph.</p>)}
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-stone-400">Relationship {q.step} of {q.total}</p>
          <p data-testid="aes-question" className="mt-1 whitespace-pre-line rounded-md border border-stone-200 bg-white p-3 text-[14px] leading-relaxed text-stone-800">{q.prompt}</p>
          <textarea data-testid="aes-field" value={field} onChange={(e) => setField(e.target.value)} onPaste={noPaste} placeholder="Answer in your own words…"
            className="mt-3 min-h-[150px] w-full resize-y rounded-lg border border-stone-200 p-4 text-[16px] leading-8 text-stone-800 focus:border-stone-400 focus:outline-none" />
          <div className="mt-3 flex items-center justify-between">
            <AboutLink />
            <div className="flex gap-2">
              <button data-testid="aes-continue-anyway" disabled={busy} onClick={continueAnyway} className="rounded-md border border-stone-200 px-3 py-2 text-[13px] text-stone-600">Continue without verifying</button>
              <button data-testid="aes-submit" disabled={busy} onClick={submit} className="rounded-md px-4 py-2 text-[14px] text-white disabled:opacity-50" style={{ background: BRAND }}>{busy ? "Checking…" : "Submit"}</button>
            </div>
          </div>
        </div></Shell>
      )}

      {screen === "established" && (
        <Shell><div data-testid="aes-established" className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></div>
          <p className="font-serif text-2xl text-stone-900">Authorship established</p>
          <p className="mt-2 text-[13px] text-stone-600">You can work with the thinking in this passage. Let's begin developing your writing.</p>
          <p data-testid="aes-established-ai-note" className="mt-3 text-[12.5px] leading-relaxed text-stone-500">The Writing Studio does not conclude from this process that you used AI in your writing. It only determines whether it has sufficient evidence to connect your revised writing to the work you had produced up to that point.</p>
          <button data-testid="aes-continue" onClick={finishAndResume} className="mt-4 rounded-md px-4 py-2 text-[13px] text-white" style={{ background: BRAND }}>Begin coaching</button>
        </div></Shell>
      )}

      {screen === "unresolved" && (
        <Shell><div data-testid="aes-unresolved" className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-stone-100 text-stone-500"><CircleSlash className="h-6 w-6" /></div>
          <p className="font-serif text-2xl text-stone-900">Authorship not established</p>
          <p className="mt-2 text-[13px] text-stone-600">I wasn't able to establish that this passage represents your own thinking and writing yet, so it stays marked as unresolved in your writing-process record. You can keep working — and establish authorship later by developing the writing with me or trying the check again.</p>
          <p data-testid="aes-unresolved-ai-note" className="mt-3 text-[12.5px] leading-relaxed text-stone-500">The Writing Studio does not conclude from this process that you used AI in your writing. It only determines whether it has sufficient evidence to connect your revised writing to the work you had produced up to that point.</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <AboutLink cls="rounded-md border border-stone-200 px-3 py-2 text-[13px] text-stone-600 inline-flex items-center gap-1" />
            <button data-testid="aes-continue" onClick={finishAndResume} className="rounded-md px-4 py-2 text-[13px] text-white" style={{ background: BRAND }}>Continue</button>
          </div>
        </div></Shell>
      )}

      {showAbout && (
        <div className="fixed inset-0 z-[68] flex items-center justify-center bg-stone-900/40 p-4" onClick={() => setShowAbout(false)}>
          <div data-testid="aes-about-modal" className="w-full max-w-lg max-h-[85vh] overflow-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8C3A2A]">How The Writing Studio establishes authorship</p>
            <p className="mt-3 whitespace-pre-line text-[13.5px] leading-relaxed text-stone-700">{ABOUT}</p>
            <div className="mt-4 text-right"><button data-testid="aes-about-close" onClick={() => setShowAbout(false)} className="rounded-md border border-stone-200 px-4 py-2 text-[13px] text-stone-600">Close</button></div>
          </div>
        </div>
      )}
    </>
  );
}
